import {
  useGetFailedPayrollJobsQuery,
  useGetFailedPayrollRecordsQuery,
  useLazyDownloadPayrollTemplateQuery,
  useUploadPayrollMutation,
  type FailedPayrollRecord,
} from '@/app/features/employee/emp-payroll';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';
import { saveBlobUrl } from './shared/payroll.helpers';

type Props = {
  open: boolean;
  handleClose: (value?: boolean) => void;
};

const columns: TableProps<FailedPayrollRecord>['columns'] = [
  {
    title: 'Row',
    dataIndex: 'row_number',
    width: 80,
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Name',
    dataIndex: 'name',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    render: (val) =>
      val ? <span className="block min-w-[120px]">{val}</span> : 'n/a',
  },
  {
    title: 'Period',
    render: (_, record) => (
      <span>
        {record.month} {record.year}
      </span>
    ),
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

/** Excel import of past payroll amounts, with its failed-rows viewer. */
export default function BulkPayrollUpload({ open, handleClose }: Props) {
  const [uploadPayroll, { isLoading: isUploading }] =
    useUploadPayrollMutation();
  const [downloadTemplate] = useLazyDownloadPayrollTemplateQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      saveBlobUrl(blobUrl, 'payroll-template.xlsx');
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadPayroll(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Bulk Payroll Upload"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedPayrollRecord>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedPayrollJobsQuery}
        useGetRecordsQuery={useGetFailedPayrollRecordsQuery}
        columns={columns}
        jobsErrorMessage="Failed to get payroll import error data"
        recordsErrorMessage="Failed to get payroll import error data"
      />
    </>
  );
}
